// The "active modifiers" a player's owned skills apply to their character roll —
// the computable slice of the bot's !skills line. Mirrors the breakdown we
// reverse-engineered: each owned skill at buyrandom step ≥1 carries a penalty
// (or "cannot use" at ≥5), the designated skillbonus skill gives +2/+5, and the
// masked "Hidden Skill" carries the step from the prestige/mark dump. The
// monster-tier / snub / recency adjustments aren't here — those are bot-internal.
import { skillModifier, formatModifier } from './fftbgLive.js';

// `skills` = parseSkills output [{token, step}]; `skillBonus` = the bonus token;
// `mark` = the marked hidden skill's step (0 = none). `nameOf` maps a token to a
// display name. Returns entries [{ token, name, step, mod, modText, isBonus,
// hidden }] in the bot's !skills FALLOFF ORDER: by `~` count (step) DESCENDING —
// the more tildes a skill has, the longer until it's usable again, so it's listed
// first. (The raw userskills file is NOT in this order, so we can't just keep file
// order.) The masked "Hidden Skill" sorts in by its `mark` step; the skillbonus
// (+2/+5, usually step 0) and the lighter penalties settle to the bottom. Sort is
// stable, so same-step skills keep file order.
// Strong and elite monsters share a cooldown per tier, and the bot lists it as one
// "Strong Monster" / "Elite Monster" line in place of those monsters' own, at the most
// tildes on any of them (see entryAdvisor monsterCooldowns). Pass `monsterTierOf(token)`
// → 'elite' | 'strong' | anything else to fold them the same way; without it each
// monster keeps its own line. A strong or elite skill bonus keeps its +2/+5 line.
export function activeModifiers(skills, skillBonus, mark, nameOf, monsterTierOf) {
  const name = (tok) => (nameOf ? nameOf(tok) : tok);
  const bonusKey = String(skillBonus || '').toLowerCase();
  const out = [], tierStep = {};
  for (const s of skills || []) {
    const isBonus = s.token.toLowerCase() === bonusKey;
    const tier = monsterTierOf && monsterTierOf(s.token);
    if (!isBonus && (tier === 'elite' || tier === 'strong')) { tierStep[tier] = Math.max(tierStep[tier] || 0, s.step || 0); continue; }
    const mod = skillModifier(s.step, isBonus);
    if (mod) out.push({ token: s.token, name: name(s.token), step: s.step, mod, modText: formatModifier(mod), isBonus, hidden: false });
  }
  for (const tier of ['elite', 'strong']) {
    const mod = skillModifier(tierStep[tier] || 0);
    if (mod) out.push({ token: '_' + tier, name: tier === 'elite' ? 'Elite Monster' : 'Strong Monster', step: tierStep[tier], mod, modText: formatModifier(mod), isBonus: false, hidden: false });
  }
  const k = Math.max(0, Math.floor(Number(mark) || 0));
  if (k >= 1) {
    const mod = skillModifier(k);
    out.push({ token: '_hidden', name: 'Hidden Skill', step: k, mod, modText: formatModifier(mod), isBonus: false, hidden: true });
  }
  out.sort((a, b) => b.step - a.step);
  return out;
}

// The 20 standard human classes (the buyrandom "class bonus" pool), in file/token
// form (no spaces).
export const STANDARD_CLASSES = [
  'Squire', 'Chemist', 'Knight', 'Archer', 'Monk', 'Priest', 'Wizard', 'TimeMage',
  'Summoner', 'Thief', 'Mediator', 'Oracle', 'Geomancer', 'Lancer', 'Samurai',
  'Ninja', 'Calculator', 'Bard', 'Dancer', 'Mime',
];

const spaced = (s) => String(s).replace(/([a-z0-9])([A-Z])/g, '$1 $2');

// The class-entry EXP bonus, from the `classbonus` dump. That file lists the
// classes you've recently cycled through, which are EXCLUDED — you earn the bonus
// for entering with no parameters or as any class NOT in it, plus your skillbonus
// skill if it's a monster (you can enter as it). The amount scales with how far
// through the class set you are: 2·⌊count/5⌋+1 (validated against the bot's "you
// can earn a +N EXP bonus for entering" line). `isMonster(token)` is injected so
// this stays data-free. Returns { amount, classes:[display names] }.
export function classBonus(classbonusLines, skillBonusToken, isMonster) {
  const excluded = new Set((classbonusLines || []).map((c) => String(c).toLowerCase().replace(/[^a-z0-9]/g, '')));
  const classes = STANDARD_CLASSES.filter((c) => !excluded.has(c.toLowerCase())).map(spaced);
  if (skillBonusToken && isMonster && isMonster(skillBonusToken)) classes.push(spaced(skillBonusToken));
  const amount = 2 * Math.floor((classbonusLines || []).length / 5) + 1;
  return { amount, classes };
}
